import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Typography,
  Tag,
  Space,
  Spin,
  Avatar,
  InputNumber,
  Grid,
  Upload,
} from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined, UploadOutlined } from '@ant-design/icons';
import { fetchProducts } from '../../store/slices/productsSlice';
import productService from '../../services/productService';
import ErrorMessage from '../common/ErrorMessage';

const { Title, Text } = Typography;
const { Option } = Select;
const { useBreakpoint } = Grid;

const ProductManager = () => {
  const dispatch = useDispatch();
  const screens = useBreakpoint();
  const { products, pagination, loading, error } = useSelector((state) => state.products);
  const categories = useSelector((state) => state.categories.categories || []);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    dispatch(fetchProducts({ page, limit: pageSize }));
  }, [dispatch, page, pageSize]);

  const handleTableChange = (pagination) => {
    setPage(pagination.current);
    setPageSize(pagination.pageSize);
  };

  const handleOpenDialog = (product = null) => {
    if (product) {
      setEditingProduct(product);
      form.setFieldsValue({
        name: product.name || '',
        description: product.description || '',
        price: product.price || '',
        discount_price: product.discount_price || '',
        stock: product.stock_quantity !== undefined ? product.stock_quantity : (product.stock || 0),
        category_id: product.category_id || null,
        image_url: product.image_url || '',
      });
    } else {
      setEditingProduct(null);
      form.resetFields();
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingProduct(null);
    form.resetFields();
  };

  const handleSubmit = async (values) => {
    try {
      const formData = new FormData();
      formData.append('name', values.name);
      formData.append('description', values.description || '');
      formData.append('price', values.price);
      formData.append('discount_price', values.discount_price || 0);
      formData.append('stock_quantity', values.stock);
      formData.append('category_id', values.category_id);

      // Handle image upload
      if (values.image && values.image[0] && values.image[0].originFileObj) {
        formData.append('product_image', values.image[0].originFileObj);
      } else if (values.image_url) {
        // Fallback if user entered URL manually or kept existing
        formData.append('image_url', values.image_url);
      }

      if (editingProduct) {
        await productService.update(editingProduct.id, formData);
      } else {
        await productService.create(formData);
      }

      handleCloseDialog();
      dispatch(fetchProducts({ page, limit: pageSize }));
    } catch (err) {
      console.error('Failed to save product:', err);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Haqiqatan ham ushbu mahsulotni o\'chirmoqchimisiz?')) {
      try {
        await productService.delete(id);
        dispatch(fetchProducts({ page, limit: pageSize }));
      } catch (err) {
        console.error('Failed to delete product:', err);
      }
    }
  };

  if (loading && !products.length) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <Spin size="large" tip="Yuklanmoqda..." />
      </div>
    );
  }

  const columns = [
    {
      title: 'Rasm',
      key: 'image',
      render: (_, record) => (
        <Avatar
          src={record.image_urls?.[0] ? `${process.env.REACT_APP_API_BASE_URL}${record.image_urls[0]}` : null}
          alt={record.name}
          shape="square"
          size={50}
        />
      ),
      responsive: ['sm'],
      width: 80,
    },
    {
      title: 'Nomi',
      dataIndex: 'name',
      key: 'name',
      render: (text) => <Text strong>{text}</Text>,
      width: 200,
    },
    {
      title: 'Kategoriya',
      key: 'category',
      render: (_, record) => record.category ? record.category.name : 'Mavjud emas',
      responsive: ['sm'],
      width: 150,
    },
    {
      title: 'Narxi',
      dataIndex: 'price',
      key: 'price',
      render: (price, record) => (
        <div>
          {record.discount_price > 0 ? (
            <>
              <Text delete type="secondary" style={{ display: 'block', fontSize: '12px' }}>
                {parseFloat(price).toLocaleString()} UZS
              </Text>
              <Text strong style={{ color: '#0891b2' }}>
                {parseFloat(record.discount_price).toLocaleString()} UZS
              </Text>
            </>
          ) : (
            <Text strong>{parseFloat(price).toLocaleString()} UZS</Text>
          )}
        </div>
      ),
      width: 150,
    },
    {
      title: 'Ombor',
      dataIndex: 'stock_quantity',
      key: 'stock',
      responsive: ['md'],
      width: 100,
    },
    {
      title: 'Holati',
      key: 'status',
      render: (_, record) => (
        <Tag color={record.stock_quantity > 0 ? 'success' : 'error'}>
          {record.stock_quantity > 0 ? 'Mavjud' : 'Tugagan'}
        </Tag>
      ),
      width: 120,
    },
    {
      title: 'Amallar',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleOpenDialog(record)}
          />
          <Button
            danger
            size="small"
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
          />
        </Space>
      ),
      align: 'right',
      width: 100,
      fixed: 'right',
    },
  ];

  return (
    <div>
      <div
        style={{
          display: 'flex',
          flexDirection: screens.xs ? 'column' : 'row',
          justifyContent: 'space-between',
          alignItems: screens.xs ? 'stretch' : 'center',
          gap: 16,
          marginBottom: 24
        }}
      >
        <Title level={2} style={{ margin: 0 }}>Mahsulotlarni boshqarish</Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => handleOpenDialog()}
          block={screens.xs}
        >
          Mahsulot qo&apos;shish
        </Button>
      </div>

      {error && <ErrorMessage message={error} />}

      <Table
        columns={columns}
        dataSource={products}
        rowKey="id"
        pagination={{
          current: page,
          pageSize: pageSize,
          total: pagination.total,
          showSizeChanger: true,
          nextIcon: 'Keyingi',
          prevIcon: 'Oldingi',
        }}
        onChange={handleTableChange}
        scroll={{ x: 1000 }}
      />

      {/* Add/Edit Dialog */}
      <Modal
        title={editingProduct ? 'Mahsulotni tahrirlash' : 'Yangi mahsulot qo\'shish'}
        open={openDialog}
        onCancel={handleCloseDialog}
        onOk={() => form.submit()}
        okText={editingProduct ? 'Yangilash' : 'Yaratish'}
        cancelText="Bekor qilish"
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="name"
            label="Mahsulot nomi"
            rules={[{ required: true, message: 'Iltimos mahsulot nomini kiriting' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="description"
            label="Tavsif"
          >
            <Input.TextArea rows={3} />
          </Form.Item>

          <Form.Item
            name="category_id"
            label="Kategoriya"
            rules={[{ required: true, message: 'Iltimos kategoriyani tanlang' }]}
          >
            <Select placeholder="Kategoriyani tanlang">
              {categories.map((category) => (
                <Option key={category.id} value={category.id}>
                  {category.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="price"
            label="Narxi (SO'M)"
            rules={[{ required: true, message: 'Iltimos narxni kiriting' }]}
          >
            <InputNumber style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="discount_price"
            label="Chegirma narxi (SO'M)"
            extra="Chegirma bo'lmasa bo'sh qoldiring yoki 0 kiriting"
          >
            <InputNumber style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="stock"
            label="Ombordagi soni"
            rules={[{ required: true, message: 'Iltimos sonini kiriting' }]}
          >
            <InputNumber style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            label="Rasm"
            extra="Rasm faylini yuklang"
          >
            <Form.Item
              name="image"
              valuePropName="fileList"
              getValueFromEvent={(e) => {
                if (Array.isArray(e)) return e;
                return e && e.fileList;
              }}
              noStyle
            >
              <Upload
                name="product_image"
                listType="picture"
                maxCount={1}
                beforeUpload={() => false}
              >
                <Button icon={<UploadOutlined />}>Yuklash uchun bosing</Button>
              </Upload>
            </Form.Item>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ProductManager;
